import { Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import StatusComponent from 'components/status/status';
import ListButton from 'components/listButton/listButton';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import NoData from 'components/noData/noData';

// services

// styles

// models
import { FinancialStatementRequest } from '../models/financialStatement';
import NotificationService from 'core/services/notification';
import { onGetFinancialStatementList } from '../services/financialStatementServices';
import TableTotal from 'components/tableTotal/tableTotal';
import { isRTL } from 'core/services/language';
import { StatusColors } from 'utils/statusColors';
import { ServiceCost } from '../../commonModels';
import { onGetServiceCost } from '../../commonServices';

interface ActionFinancialStatementListPageProps {}

const ActionFinancialStatementListPage: FunctionComponent<ActionFinancialStatementListPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<FinancialStatementRequest[]>([]);

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'code',
			key: 'code',
			render: (code) => <>#{code}</>,
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'date',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status, record) => (
				<StatusComponent text={isRTL() ? record.translated_status.ar : record.translated_status.en} color={StatusColors[status]} />
			),
		},
		{
			title: t('ACTIONS.DOCUMENT_TYPE'),
			dataIndex: 'period',
			key: 'period',
			render: (_, record) => (
				<>
					{record.year} - {isRTL() ? record.translated_period_type.ar : record.translated_period_type.en}
				</>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => <ListButton type="view" onClick={() => navigate(`${record.id}`)} />,
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getFinancialStatements();
		getServiceCost();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const response = await onGetServiceCost('financial_statement');
			setServiceCost(response?.success);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getFinancialStatements = async () => {
		try {
			setIsLoading(true);

			const response = await onGetFinancialStatementList(getCurrentPage());

			setRequests(response.financial_statement_actions);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetFinancialStatementList(getCurrentPage());
			setRequests(response.financial_statement_actions);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.FINANCIAL_STATEMENT')}
				subTitle={t('ACTIONS.TO_ADD_FINANCIAL_STATEMENTS')}
				costLoading={serviceCostLoading}
				costs={[serviceCost!]}
			/>

			<Table
				columns={columns}
				dataSource={requests}
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				locale={{ emptyText: <NoData /> }}
			/>
		</>
	);
};

export default ActionFinancialStatementListPage;
